import { useEffect, useRef, useState, KeyboardEvent } from "react";

interface InputProps {
  type?: "text" | "password";
  placeholder?: string;
  label?: string;
  value: string;
  onTextChange: (text: string) => void;
  height?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  multiLine?: boolean;
  className?: string;
  inputMode?: "text" | "email" | "numeric";
  autoComplete?: string;
  pattern?: string;
  onKeyPress?: (
    e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

const Input = ({
  type = "text",
  placeholder,
  label,
  value,
  onTextChange,
  height,
  disabled = false,
  autoFocus = false,
  multiLine = false,
  className = "",
  inputMode,
  autoComplete,
  pattern,
  onKeyPress,
}: InputProps) => {
  const [focused, setFocused] = useState(false);
  const fieldRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Focusing synchronously on mount races the native keyboard against the
  // webview resize on Android, which can leave a bottom-sheet modal laid out
  // against a stale viewport with the field pushed off-screen. Focus once
  // layout has settled and make sure the field ends up visible.
  useEffect(() => {
    if (!autoFocus || disabled) return;
    const timeout = setTimeout(() => {
      const field = fieldRef.current;
      if (!field) return;
      field.focus();
      field.scrollIntoView({ block: "center" });
    }, 250);
    return () => clearTimeout(timeout);
  }, [autoFocus, disabled]);

  // The field is a recess in the surface it sits on, not another floating
  // pane: a neutral fill plus the same top light line, and focus is carried by
  // the ring rather than by a border appearing.
  const baseClasses = `
    field
    rounded-3xl
    px-6
    text-lg
    font-medium
    tracking-wide
    ${focused && !disabled ? "bg-[var(--fill-2)]" : "hover:bg-[var(--fill-2)]"}
    ${disabled ? "opacity-50 cursor-not-allowed" : ""}
  `;

  const inputClasses = `${baseClasses} ${multiLine ? "py-4 leading-relaxed resize-none" : "h-16"} ${className}`;

  return (
    <div className="w-full">
      {label && (
        <label className="block mb-2 text-sm font-medium text-[var(--text-secondary)] tracking-wide">
          {label}
        </label>
      )}
      {multiLine ? (
        <textarea
          dir="auto"
          data-action-id="input.edit"
          ref={(el) => {
            fieldRef.current = el;
          }}
          onChange={(e) => onTextChange(e.target.value)}
          value={value}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          onKeyDown={onKeyPress}
          className={inputClasses}
          style={{ height: height || "180px" }}
        />
      ) : (
        <input
          dir={type === "password" ? undefined : "auto"}
          data-action-id="input.edit"
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          pattern={pattern}
          ref={(el) => {
            fieldRef.current = el;
          }}
          onChange={(e) => onTextChange(e.target.value)}
          value={value}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          onKeyDown={onKeyPress}
          className={inputClasses}
          style={height ? { height } : undefined}
        />
      )}
    </div>
  );
};

export default Input;
